import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Grid, 
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent
} from '@mui/material';
import { 
  Receipt,
  Build,
  CreditCard,
  Security,
  CheckCircle 
} from '@mui/icons-material';
import OnboardingLayout from '../../components/host/OnboardingLayout';
import { NavigationButtons, StyledButton, InfoCard } from '../../components/host/OnboardingComponents';
import api from '../../services/api/axiosConfig';
import useCurrentUser from '../../hooks/useCurrentUser';

interface OrderSummary {
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  total: number;
}

interface PaymentData {
  financingOption: 'full' | '12months' | '24months' | '36months';
  installationOption: 'self' | 'professional';
}

const HostOnboardingStep4 = () => {
  const navigate = useNavigate();
  const { user, userId, loading: userLoading } = useCurrentUser();
  const [orderSummary, setOrderSummary] = useState<OrderSummary>({
    items: [],
    total: 0
  });
  const [paymentData, setPaymentData] = useState<PaymentData>({
    financingOption: 'full',
    installationOption: 'professional'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load data from previous steps
    const step3Data = localStorage.getItem('hostOnboardingStep3');
    if (step3Data) {
      const subscriptionData = JSON.parse(step3Data);
      
      // Calculate order summary based on subscription data
      const items = [
        {
          name: 'Botels Selected',
          quantity: subscriptionData.quantity,
          price: subscriptionData.totalCost / subscriptionData.quantity
        },
        
      ];
      
      const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      setOrderSummary({ items, total });
    }
  }, []);

  const handlePrevious = () => {
    navigate('/host/onboarding/step-3');
  };

  const handleComplete = async () => {
    setLoading(true);
    
    try {
      // Collect all onboarding data from localStorage
      const step1Data = JSON.parse(localStorage.getItem('hostOnboardingStep1') || '{}');
      const step2Data = JSON.parse(localStorage.getItem('hostOnboardingStep2') || '{}');
      const step3Data = JSON.parse(localStorage.getItem('hostOnboardingStep3') || '{}');
      
      // First, create subscription
      // Calculate subscription dates (1 year from now)
      const startDate = new Date();
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
      
      // Get plan features based on selected plan
      const planFeaturesMap: { [key: string]: string[] } = {
        'basic': ['Up To 5 Botels', 'Basic Analytics', 'Email Support'],
        'premium': ['Up To 15 Botels', 'Advanced Analytics', '24/7 Support'],
        'enterprise': ['Up to 50 Botels', 'Custom Analytics', 'Priority Support'],
        'partner': ['Unlimited Botels', 'White Label Solution', 'Dedicated Account Manager']
      };
      
      // Prepare subscription data
      // Handle totalCost - it can be a number or "Custom Pricing" string
      let subscriptionAmount = 0;
      if (typeof step3Data.totalCost === 'number') {
        subscriptionAmount = step3Data.totalCost;
      } else if (typeof step3Data.totalCost === 'string' && step3Data.totalCost !== 'Custom Pricing') {
        // Try to parse if it's a string number
        subscriptionAmount = parseFloat(step3Data.totalCost) || 0;
      }
      
      // Combine all data for application submission
      const completeOnboardingData = {
        ...step1Data,
        ...step2Data,
        ...step3Data,
        ...paymentData
      };

      // Import the host service
      const { hostService } = await import('../../services/hostService');
      
      // Get userId from useCurrentUser hook (fetches from API)
      let currentUserId = userId;
      
      // If no userId from hook, submit application first to get userId
      let applicationResponse;
      if (!currentUserId) {
        applicationResponse = await hostService.submitApplication(completeOnboardingData);
        
        if (!applicationResponse.success) {
          throw new Error(applicationResponse.message || 'Application submission failed');
        }
        
        currentUserId = applicationResponse.data?.user?.id;
        
        if (!currentUserId) {
          console.error('Application response:', applicationResponse);
          throw new Error('Failed to get user ID from application response');
        }
        
      }
      
      // Prepare subscription payload with userId (required by API)
      const subscriptionPayload = {
        planName: step3Data.selectedPlan || 'premium',
        amount: subscriptionAmount,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        planDetails: {
          features: planFeaturesMap[step3Data.selectedPlan] || planFeaturesMap['premium'],
          quantity: step3Data.quantity || 1
        },
        paymentMethod: paymentData.financingOption === 'full' ? 'credit_card' : 'financing',
        userId: currentUserId! // userId is guaranteed to be set at this point
      };
      
      
      // Call subscription API with userId
      await api.post('/subscriptions', subscriptionPayload, {
        skipRedirect: true
      } as any);
      
      // After subscription is created, submit the application (if not already done above)
      let response = applicationResponse;
      if (!response) {
        response = await hostService.submitApplication(completeOnboardingData);
      }
      
      if (response.success) {
        // Get partnerId from application response
        const partnerId = response.data?.hostPartner?.id;
        
        if (!partnerId) {
          throw new Error('Failed to get partner ID from application response');
        }
        
        // Place order after subscription and application are created
        const units = step3Data.quantity || 1;
        const unitPrice = subscriptionAmount / units; // Price per unit
        
        const orderPayload = {
          partnerId: partnerId,
          email: step2Data.emailAddress || '',
          phone: step2Data.phoneNumber || '',
          units: units,
          unitPrice: unitPrice,
          total_amount: subscriptionAmount * 100,
          productId: 'botel',
          version: 'v1.0.0',
          promoCode: null
        };
        
        
        // Check if token exists
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No authentication token found. Attempting to place order anyway...');
        }
        
        try {
          await api.post('/partners/order', orderPayload, {
            skipRedirect: true
          } as any);
        } catch (orderError: any) {
          console.error('Failed to place order:', orderError);
          
          // If it's a 401 error and no token exists, log a helpful message
          if (orderError.response?.status === 401 && !token) {
            console.warn('Order API requires authentication. The user may need to log in first, or the order API may need to be updated to accept partnerId without authentication during onboarding.');
          }
          
          // Don't block the flow if order fails - subscription and application are already created
          // The order can be placed later after the user logs in
        }
        
        // Store final data
        localStorage.setItem('hostOnboardingStep4', JSON.stringify(paymentData));
        localStorage.setItem('hostApplicationResponse', JSON.stringify(response.data));
        
        // Navigate to success page
        navigate('/host/onboarding/success');
      } else {
        throw new Error(response.message || 'Application submission failed');
      }
    } catch (error: any) {
      console.error('Order processing failed:', error);
      // You might want to show an error message to the user here
      alert(error.message || 'Failed to process order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFinancingAmount = () => {
    const { total } = orderSummary;
    switch (paymentData.financingOption) {
      case '12months':
        return (total / 12).toFixed(2);
      case '24months':
        return (total / 24).toFixed(2);
      case '36months':
        return (total / 36).toFixed(2);
      default:
        return total.toFixed(2);
    }
  };

  const getFinancingLabel = () => {
    switch (paymentData.financingOption) {
      case '12months':
        return '12 Months';
      case '24months':
        return '24 Months';
      case '36months':
        return '36 Months';
      default:
        return 'Pay In Full';
    }
  };

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={4}
      stepTitle="Complete Your Botel"
      stepSubtitle="Review your order and complete the setup"
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Order Summary */}
        <InfoCard title="Order Summary" icon={<Receipt />}>
          <Box sx={{ mb: 3 }}>
            {orderSummary.items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  {item.quantity} × ${item.price.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                  ${(item.price * item.quantity).toFixed(2)}
                </Typography>
              </Box>
            ))}
            
            <Divider sx={{ my: 2, backgroundColor: '#4a5568' }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Total
              </Typography>
              <Typography variant="h6" sx={{ color: '#377dff', fontWeight: 700 }}>
                ${orderSummary.total.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </InfoCard>

        {/* Financing Options */}
        <InfoCard title="Financing Options" icon={<CreditCard />}>
          <RadioGroup
            value={paymentData.financingOption}
            onChange={(e) => setPaymentData(prev => ({ ...prev, financingOption: e.target.value as any }))}
          >
            <FormControlLabel
              value="full"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <Typography sx={{ color: '#fff' }}>Pay In Full - ${orderSummary.total.toFixed(2)}</Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.financingOption === 'full' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.financingOption === 'full' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
            
            <FormControlLabel
              value="12months"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <Typography sx={{ color: '#fff' }}>12 Months - ${(orderSummary.total / 12).toFixed(2)}/Mo</Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.financingOption === '12months' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.financingOption === '12months' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
            
            <FormControlLabel
              value="24months"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <Typography sx={{ color: '#fff' }}>24 Months - ${(orderSummary.total / 24).toFixed(2)}/Mo</Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.financingOption === '24months' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.financingOption === '24months' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
            
            <FormControlLabel
              value="36months"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <Typography sx={{ color: '#fff' }}>36 Months $22.99/Mo</Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.financingOption === '36months' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.financingOption === '36months' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
          </RadioGroup>
        </InfoCard>

        {/* Installation Preferences */}
        <InfoCard title="Installation Preferences" icon={<Build />}>
          <RadioGroup
            value={paymentData.installationOption}
            onChange={(e) => setPaymentData(prev => ({ ...prev, installationOption: e.target.value as any }))}
          >
            <FormControlLabel
              value="self"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>Self-Install</Typography>
                  <Typography sx={{ color: '#b0b8c1', fontSize: '0.875rem' }}>
                    Install Your Botel Yourself With Our Detailed Guide
                  </Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.installationOption === 'self' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.installationOption === 'self' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
            
            <FormControlLabel
              value="professional"
              control={<Radio sx={{ color: '#4a5568', '&.Mui-checked': { color: '#377dff' } }} />}
              label={
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>BellHop Express Pro Install - $149.99</Typography>
                  <Typography sx={{ color: '#b0b8c1', fontSize: '0.875rem' }}>
                    Professional Installation By Certified Technicians
                  </Typography>
                </Box>
              }
              sx={{ 
                mb: 2, 
                p: 2, 
                border: paymentData.installationOption === 'professional' ? '2px solid #377dff' : '1px solid #4a5568',
                borderRadius: 2,
                backgroundColor: paymentData.installationOption === 'professional' ? 'rgba(55, 125, 255, 0.1)' : 'transparent'
              }}
            />
          </RadioGroup>
        </InfoCard>

        {/* Payment Summary */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #377dff, #2563eb)',
            borderRadius: 3,
            mb: 4
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
              Ready to Complete Your Order?
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                {getFinancingLabel()}: <strong>${getFinancingAmount()}</strong>
                {paymentData.financingOption !== 'full' && '/month'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Installation: {paymentData.installationOption === 'professional' ? 'Professional ($149.99)' : 'Self-Install (Free)'}
              </Typography>
            </Box>
            
            <StyledButton
              variant="primary"
              size="large"
              onClick={handleComplete}
              disabled={loading}
              sx={{ 
                backgroundColor: '#fff',
                color: '#377dff',
                px: 6, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              {loading ? 'Processing...' : 'Confirm & Pay'} 🔒
            </StyledButton>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Security sx={{ color: '#4ade80', mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#4ade80' }}>
              Secure Payment Processed By Stripe
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Your payment information is encrypted and secure
          </Typography>
        </Box>

        {/* Navigation */}
        <NavigationButtons
          onPrevious={handlePrevious}
          previousLabel="Back to Plans"
        />
      </Box>
    </OnboardingLayout>
  );
};

export default HostOnboardingStep4;
